import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ClientFolder from "../components/crm/ClientFolder";
import Missions from "../components/crm/Missions";
import { useButtonStore } from "../stores/components/button";
import { useCrmStore } from "../stores/crm";
import { showToastError } from "../services/toast";
import {
  getCustomerFiles,
  getDocumentsByMission,
  getMissions,
} from "../api/customerFile";
import TableCRM from "../components/ui/tables/TableCRM";
import { useUtilsStore } from "../stores/components/utils";
import { useNavigate } from "react-router-dom";
import { useLoaderStore } from "../stores/components/loader";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import {
  ActionButton,
  CloseButton,
  ContextBadge,
  PageHeader,
  PagePanel,
  PageShell,
  PanelCenter,
  PanelToolbar,
  PlusIcon,
  ResultCount,
  SearchField,
  ToolbarRow,
} from "../components/ui/page/PageShell";

const CRM_TABS = ["Dossier clients", "Missions", "Documents"];

const COLUMNS_BY_TAB: Record<string, { header: string; accessor: string }[]> = {
  "Dossier clients": [
    { header: "Nom", accessor: "name" },
    { header: "", accessor: "actions" },
    { header: "Missions", accessor: "missionsAmount" },
    { header: "Date", accessor: "createdAt" },
    { header: "Solde TTC", accessor: "balance" },
  ],
  Missions: [
    { header: "Service", accessor: "department" },
    { header: "", accessor: "actions" },
    { header: "Date début", accessor: "startDate" },
    { header: "Date de fin", accessor: "endDate" },
    { header: "Budget", accessor: "budgetEuros" },
    { header: "HT d'abonnement", accessor: "periodicAmountExclVAT" },
    { header: "Solde", accessor: "balance" },
  ],
  Documents: [
    { header: "Nom", accessor: "documentFileName" },
    { header: "Date", accessor: "date" },
    { header: "Montant TTC", accessor: "amount" },
    { header: "Taille", accessor: "documentSize" },
    { header: "Relance", accessor: "followUpDate" },
  ],
};

const CREATE_LABEL_BY_TAB: Record<string, string> = {
  "Dossier clients": "Ajouter un client",
  Missions: "Ajouter une mission",
};

const SEARCH_PLACEHOLDER_BY_TAB: Record<string, string> = {
  "Dossier clients": "Rechercher un client, un solde…",
  Missions: "Rechercher un service, un montant…",
  Documents: "Rechercher un document, une date…",
};

const ROW_LABEL_BY_TAB: Record<string, [string, string]> = {
  "Dossier clients": ["dossier client", "dossiers clients"],
  Missions: ["mission", "missions"],
  Documents: ["document", "documents"],
};

const Crm = () => {
  // Store
  const {
    tabCRM,
    setTabCRM,
    clientFiles,
    setClientFiles,
    viewOrUpdate,
    missions,
    setMissions,
    idCustomer,
    idMission,
    dataFilter,
    setDataFilter,
    dataFilterMission,
    setDataFilterMission,
    dataFilterDocument,
    setDataFilterDocument,
    documents,
    setDocuments,
    updateCrm,
    clientName,
  } = useCrmStore();
  const { searchTerm, setSearchTerm } = useUtilsStore();
  const { createButton, setCreateButton } = useButtonStore();
  const navigate = useNavigate();
  const { isLoading, setIsLoading } = useLoaderStore();
  const { t } = useTranslation();

  // Permet de savoir si on est en train de créer ou de modifier
  const isCreatingOrUpdating = createButton || viewOrUpdate;

  // Appel de l'API en fonction de la valeur de tab
  useEffect(() => {
    if (tabCRM === "Dossier clients" && !isCreatingOrUpdating) {
      setIsLoading(true);
      // Réinitialise les données
      setDataFilterMission([]);
      setMissions([]);
      setSearchTerm("");
      getCustomerFiles()
        .then((res) => {
          setClientFiles(res.data);
          setIsLoading(false);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
          setIsLoading(false);
        });
    } else if (tabCRM === "Missions") {
      setIsLoading(true);
      getMissions(idCustomer)
        .then((res) => {
          setIsLoading(false);
          setSearchTerm("");
          // Affiche les missions ouvertes en premier
          const sortDatabyClosed = res.data.sort(
            (a: any, b: any) => Number(a.closed) - Number(b.closed)
          );
          // Trie les missions par nom de service
          const sortDataByName = sortDatabyClosed.sort((a: any, b: any) =>
            a.department.localeCompare(b.department)
          );
          // Trie les missions par date de fin
          const sortDataByStartDate = sortDataByName.sort(
            (a: any, b: any) =>
              new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
          );
          setMissions(sortDataByStartDate);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
          setIsLoading(false);
        });
    }
  }, [createButton, viewOrUpdate, tabCRM, updateCrm]);

  // Récupère les documents de la mission
  useEffect(() => {
    if (tabCRM === "Documents" && idMission !== 0) {
      setIsLoading(true);
      getDocumentsByMission(idCustomer, idMission)
        .then((resMission) => {
          setSearchTerm("");
          const mergedDocuments = [...resMission.data];
          setDocuments(mergedDocuments);
          setIsLoading(false);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
          setIsLoading(false);
        });
    }
  }, [idMission]);

  // Filtre les données en fonction de la recherche
  useEffect(() => {
    let data = [];
    if (tabCRM === "Dossier clients") {
      data = clientFiles?.length > 0 ? [...clientFiles] : [...dataFilter];

      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["name"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["balance"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );
        setDataFilter(data);
      } else {
        setDataFilter(clientFiles);
      }
    } else if (tabCRM === "Missions") {
      data = missions?.length > 0 ? [...missions] : [...dataFilterMission];
      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["department"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["invoiceNumber"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["commenditaire"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["nameDepartment"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["amountExclVAT"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["totalAmount"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["balance"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );
        setDataFilterMission(data);
      } else {
        setDataFilterMission(missions);
      }
    } else if (tabCRM === "Documents") {
      data = documents?.length > 0 ? [...documents] : [...dataFilterDocument];
      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["documentFileName"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["date"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );
        setDataFilterDocument(data);
      } else {
        setDataFilterDocument(documents);
      }
    }
  }, [searchTerm, documents, missions, clientFiles]);

  // Fermeture de l'onglet et retour à la page précédente
  const handleCloseTab = () => {
    if (tabCRM === "Missions") {
      setTabCRM("Dossier clients");
    } else if (tabCRM === "Documents") {
      setTabCRM("Missions");
    } else {
      navigate("/dashboard");
    }
  };

  const activeColumns = COLUMNS_BY_TAB[tabCRM];
  const activeData =
    tabCRM === "Dossier clients"
      ? dataFilter
      : tabCRM === "Missions"
        ? dataFilterMission
        : tabCRM === "Documents"
          ? dataFilterDocument
          : [];
  const rowCount = activeData?.length ?? 0;
  const [singular, plural] = ROW_LABEL_BY_TAB[tabCRM] ?? ["élément", "éléments"];
  const createLabel = CREATE_LABEL_BY_TAB[tabCRM];

  // Fil d'Ariane : les onglets déjà traversés restent cliquables
  const currentTabIndex = CRM_TABS.indexOf(tabCRM);
  const breadcrumb = CRM_TABS.slice(0, currentTabIndex + 1);

  return (
    <PageShell>
      {isCreatingOrUpdating ? (
        tabCRM === "Dossier clients" ? (
          <PagePanel>
            <ClientFolder />
          </PagePanel>
        ) : tabCRM === "Missions" ? (
          <PagePanel>
            <Missions />
          </PagePanel>
        ) : null
      ) : (
        <>
          <PageHeader
            eyebrow={
              <nav
                aria-label="Fil d'Ariane"
                className="flex flex-wrap items-center gap-x-1 gap-y-0.5"
              >
                {breadcrumb.map((crumb, index) => {
                  const isCurrent = index === currentTabIndex;
                  return (
                    <span key={crumb} className="flex items-center gap-1">
                      {index > 0 && (
                        <span aria-hidden="true" className="text-slate-300">
                          /
                        </span>
                      )}
                      {isCurrent ? (
                        <span className="text-slate-600">{t(crumb)}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setTabCRM(crumb)}
                          className="cursor-pointer rounded transition-colors hover:text-indigo-600 focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:outline-none"
                        >
                          {t(crumb)}
                        </button>
                      )}
                    </span>
                  );
                })}
              </nav>
            }
            title={t(tabCRM)}
            badge={
              tabCRM !== "Dossier clients" && clientName ? (
                <ContextBadge>{clientName}</ContextBadge>
              ) : null
            }
            actions={
              <>
                {createLabel ? (
                  <ActionButton
                    variant="primary"
                    icon={<PlusIcon />}
                    onClick={() => setCreateButton(!createButton)}
                  >
                    {createLabel}
                  </ActionButton>
                ) : null}
                <CloseButton
                  onClick={handleCloseTab}
                  label={
                    currentTabIndex > 0
                      ? "Revenir au niveau précédent"
                      : "Retour au tableau de bord"
                  }
                />
              </>
            }
          />

          <PagePanel>
            <PanelToolbar>
              <ToolbarRow>
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={SEARCH_PLACEHOLDER_BY_TAB[tabCRM]}
                />
                <ResultCount
                  count={rowCount}
                  singular={singular}
                  plural={plural}
                  filtered={searchTerm !== ""}
                />
              </ToolbarRow>
            </PanelToolbar>

            {/* Affiche les tableaux en fonction de la valeur tab sinon affiche le spinner */}
            {isLoading ? (
              <PanelCenter>
                <LoadingSpinner size="large" text="Chargement des données..." />
              </PanelCenter>
            ) : activeColumns ? (
              <TableCRM columns={activeColumns} data={activeData ?? []} />
            ) : null}
          </PagePanel>
        </>
      )}
    </PageShell>
  );
};

export default Crm;
